import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import moment from "moment";
import {
  DestinationType,
  DestinationListSearchProps,
  DestinationSetType,
  DestinationEditProps,
} from "./types";
import { Account } from "../../../lib/interfaces";
import {
  fetchDestinationListRoute,
  fetchDestinationSetsRoute,
  fetchAccountsByNameRoute,
  fetchDestinationVersionsRoute,
  createDestinationRoute,
  fetchDestinationByIdRoute,
} from "../../../lib/routes/destinations";
import { LoadingStatusCode } from "../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../lib/utils/catchApiError";

interface InitialState {
  destinationListFilter: DestinationListSearchProps;
  destination?: DestinationType;
  destinationLoading: LoadingStatusCode;
  parentDestination?: DestinationType;
  parentDestinationLoading: LoadingStatusCode;
  destinationList: DestinationType[];
  destinationListLoading: LoadingStatusCode;
  parentDestinationList: DestinationType[];
  parentDestinationListLoading: LoadingStatusCode;
  destinationVersions: DestinationType[];
  destinationVersionsLoading: LoadingStatusCode;
  destinationListTotalElements: number;
  destinationSets: DestinationSetType[];
  destinationSetsLoading: LoadingStatusCode;
  searchedAccounts: Account[];
  searchedAccountsLoading: LoadingStatusCode;
}

export const initialDestinationListFilter: DestinationListSearchProps = {
  accountId: undefined,
  areaCode: undefined,
  destinationSetId: undefined,
  destinationTypeId: undefined,
  name: undefined,
  onDate: moment().format(),
  shortName: undefined,
  showClosed: undefined,
  filterOpen: false,
  page: 1,
  size: 10,
};

const initialState: InitialState = {
  destinationListFilter: initialDestinationListFilter,
  destinationLoading: "idle",
  parentDestinationLoading: "idle",
  destinationList: [],
  destinationListLoading: "idle",
  parentDestinationList: [],
  parentDestinationListLoading: "idle",
  destinationVersions: [],
  destinationVersionsLoading: "idle",
  destinationListTotalElements: 0,
  destinationSets: [],
  destinationSetsLoading: "idle",
  searchedAccounts: [],
  searchedAccountsLoading: "idle",
};

// Получение списка направлений (поиск)
export const fetchDestinationList = createAsyncThunk(
  "destinations/fetchDestinationList",
  async (params: DestinationListSearchProps) => {
    try {
      const response = axios.get(fetchDestinationListRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка направлений. Попробуйте позже",
      });
    }
  }
);

// Получение списка родительских направлений
export const fetchParentDestinationList = createAsyncThunk(
  "destinations/fetchParentDestinationList",
  async (params: DestinationListSearchProps) => {
    try {
      const response = axios.get(fetchDestinationListRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка родительских направлений. Попробуйте позже",
      });
    }
  }
);

// Получение версий направления
export const fetchDestinationVersions = createAsyncThunk(
  "destinations/fetchDestinationVersions",
  async (destinationId: number) => {
    try {
      const response = axios.get(fetchDestinationVersionsRoute(destinationId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки версий направления. Попробуйте позже",
      });
    }
  }
);

// Получение списка наборов направлений (справочник)
export const fetchDestinationSets = createAsyncThunk(
  "destinations/fetchDestinationSets",
  async () => {
    try {
      const response = axios.get(fetchDestinationSetsRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка наборов направлений. Попробуйте позже",
      });
    }
  }
);

// Получение списка аккаунтов в поиске (сделано здесь, несмотря на нелогичность. На старую реализацию в accounts.js невозможно смотреть без слез)
export const fetchAccountsByName = createAsyncThunk(
  "destinations/fetchAccountsByName",
  async (params: { name: string; page?: number; size?: number }) => {
    const { name, page = 0, size = 20 } = params;
    try {
      const response = axios.get(fetchAccountsByNameRoute({ name, page, size }));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка аккаунтов. Попробуйте позже",
      });
    }
  }
);

// Создание направления
export const createDestination = createAsyncThunk(
  "destinations/createDestination",
  async (params: DestinationEditProps) => {
    try {
      const response = axios.post(createDestinationRoute(), params);
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания направления. Попробуйте позже",
      });
    }
  }
);

// Обновлений направления
export const updateDestination = createAsyncThunk(
  "destinations/updateDestination",
  async (params: DestinationEditProps) => {
    try {
      const response = axios.put(createDestinationRoute(), params);
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления направления. Попробуйте позже",
      });
    }
  }
);

// Загрузка направления по id
export const fetchDestinationById = createAsyncThunk(
  "destinations/fetchDestinationById",
  async (id: number) => {
    try {
      const response = axios.get(fetchDestinationByIdRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки направления. Попробуйте позже",
      });
    }
  }
);

// Загрузка родительского направления по id
export const fetchParentDestinationById = createAsyncThunk(
  "destinations/fetchParentDestinationById",
  async (id: number) => {
    try {
      const response = axios.get(fetchDestinationByIdRoute(id));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки родительского направления. Попробуйте позже",
      });
    }
  }
);

export const slice = createSlice({
  name: "destinations",
  initialState,
  reducers: {
    cleanDestinationList: (state) => {
      state.destinationList = [];
      state.destinationListTotalElements = 0;
      state.destinationListLoading = "idle";
    },
    cleanParentDestinationList: (state) => {
      state.parentDestinationListLoading = "idle";
      state.parentDestinationList = [];
    },
    cleanDestinationVersions: (state) => {
      state.destinationVersions = [];
      state.destinationVersionsLoading = "idle";
    },
    cleanAccountSelect: (state) => {
      state.searchedAccounts = [];
      state.searchedAccountsLoading = "idle";
    },
    setDestinationListFilter: (state, { payload }) => {
      state.destinationListFilter = payload;
    },
    resetDestinationListFilter: (state) => {
      state.destinationListFilter = { ...initialDestinationListFilter, filterOpen: true };
    },
    setParentDestinationList: (state, { payload }) => {
      state.parentDestinationListLoading = "fulfilled";
      state.parentDestinationList = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDestinationById.pending, (state) => {
        state.destinationLoading = "pending";
      })
      .addCase(fetchDestinationById.rejected, (state) => {
        state.destinationLoading = "rejected";
        state.destination = undefined;
      })
      .addCase(fetchDestinationById.fulfilled, (state, { payload }) => {
        state.destinationLoading = "fulfilled";
        state.destination = payload;
      })
      .addCase(fetchParentDestinationById.pending, (state) => {
        state.parentDestinationLoading = "pending";
      })
      .addCase(fetchParentDestinationById.rejected, (state) => {
        state.parentDestinationLoading = "rejected";
        state.parentDestination = undefined;
      })
      .addCase(fetchParentDestinationById.fulfilled, (state, { payload }) => {
        state.parentDestinationLoading = "fulfilled";
        state.parentDestination = payload;
      })
      .addCase(fetchDestinationList.pending, (state) => {
        state.destinationListLoading = "pending";
      })
      .addCase(fetchDestinationList.rejected, (state) => {
        state.destinationListLoading = "rejected";
        state.destinationListTotalElements = 0;
        state.destinationList = [];
      })
      .addCase(fetchDestinationList.fulfilled, (state, { payload }) => {
        state.destinationListLoading = "fulfilled";
        state.destinationList = payload.content;
        state.destinationListTotalElements = payload.totalElements;
      })
      .addCase(fetchParentDestinationList.pending, (state) => {
        state.parentDestinationListLoading = "pending";
      })
      .addCase(fetchParentDestinationList.rejected, (state) => {
        state.parentDestinationListLoading = "rejected";
        state.parentDestinationList = [];
      })
      .addCase(fetchParentDestinationList.fulfilled, (state, { payload }) => {
        state.parentDestinationListLoading = "fulfilled";
        state.parentDestinationList = payload.content;
      })
      .addCase(fetchDestinationSets.pending, (state) => {
        state.destinationSetsLoading = "pending";
      })
      .addCase(fetchDestinationSets.rejected, (state) => {
        state.destinationSetsLoading = "rejected";
        state.destinationSets = [];
      })
      .addCase(fetchDestinationSets.fulfilled, (state, { payload }) => {
        state.destinationSetsLoading = "fulfilled";
        state.destinationSets = payload;
      })
      .addCase(fetchAccountsByName.pending, (state) => {
        state.searchedAccountsLoading = "pending";
      })
      .addCase(fetchAccountsByName.rejected, (state) => {
        state.searchedAccountsLoading = "rejected";
        state.searchedAccounts = [];
      })
      .addCase(fetchAccountsByName.fulfilled, (state, { payload }) => {
        state.searchedAccountsLoading = "fulfilled";
        state.searchedAccounts = payload.content;
      })
      .addCase(fetchDestinationVersions.pending, (state) => {
        state.destinationVersionsLoading = "pending";
      })
      .addCase(fetchDestinationVersions.rejected, (state) => {
        state.destinationVersionsLoading = "rejected";
        state.destinationVersions = [];
      })
      .addCase(fetchDestinationVersions.fulfilled, (state, { payload }) => {
        state.destinationVersionsLoading = "fulfilled";
        state.destinationVersions = payload.sort((a, b) =>
          moment(a.toDate).isAfter(moment(b.toDate)) ? -1 : 1
        );
      });
  },
});

export const { reducer: destinationsReducer } = slice;
export const {
  cleanAccountSelect,
  cleanDestinationList,
  cleanDestinationVersions,
  setDestinationListFilter,
  resetDestinationListFilter,
  setParentDestinationList,
  cleanParentDestinationList,
} = slice.actions;
